import React from 'react';
import { observer } from 'mobx-react';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { DateTimeValue, formatFloating, formatMoment, formatMomentDate } from 'utils/formatters/timezone';

import { Value } from 'components/SharedFormComponents/EditableTextInput/styles';
import { LocationAnswerType, UuidableName } from 'utils/api/types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EnumOptions } from 'utils/api/modules/entities/entities.types';

import UserRow from 'components/UserRow';

import EntityDisplayValue from 'components/Entities/EntityDisplayValue';

import { useStore } from 'utils/hooks/store';
import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, UserRole, ProtectorType } from 'core/constants';

import { isRated, RiskMatrixDisplay } from 'components/RiskMatrix';

import type { RiskMatrixValue } from 'components/RiskMatrix';

import Logger from 'utils/logger';

import API from 'utils/api/api';

import CustomAttachmentDisplayValue from 'containers/EntitiesFormSubmission/components/CustomAttachmentField/CustomAttachmentDisplayValue';
import { RepeatingGroupDisplay } from 'containers/EntitiesFormSubmission/components/RepeatingGroupField';

import { DescriptionField, LocationText } from 'components/SharedFormComponents/styles';

import { LookupAnswerEntry, resolveLookupEntries } from 'components/Audits/lookupRecords';
import ChoiceAnswerChips, { ChoiceAnswerChipEntry } from 'components/Audits/ChoiceAnswerChips';
import { optionChoiceTone, YES_NO_NA_TONE } from 'components/Audits/choiceTone';

import PartnerAnswer, { PartnerValue } from 'components/SharedFormComponents/PartnerAnswer';

import { CustomFieldDisplayValueProps } from './types';
import { EmptyValue } from './styles';

const YES_NO_NA_LABELS: Record<string, string> = { yes: 'Yes', no: 'No', na: 'N/A' };

// An option deleted from the template leaves a key that no longer maps, so the cast is a lie:
// drop those holes and blanks, but keep anything else the author chose to label.
const toLabelList = (value: AnswerTypes): string[] => {
  const entries = (Array.isArray(value) ? value : [value]) as (string | null | undefined)[];

  return entries.filter((label): label is string => label !== undefined && label !== null && label !== '');
};

const toUntonedEntry = (label: string): ChoiceAnswerChipEntry => ({ label });

// A key the template no longer has is dropped, the same way the label holes above are.
const toTonedEntries = (value: AnswerTypes, options: EnumOptions): ChoiceAnswerChipEntry[] => {
  const byKey = new Map(options.map((option) => [option.key, option]));

  return toLabelList(value).flatMap((key) => {
    const option = byKey.get(key);

    return option ? [{ label: option.value, tone: optionChoiceTone(option.colour) }] : [];
  });
};

const CustomFieldDisplayValue: React.FunctionComponent<CustomFieldDisplayValueProps> = ({
  type,
  value,
  schema,
  lookupType,
  label,
  isImported = false,
  submissionTab = false,
  repeatingGroup,
  choiceChips = false,
  choiceOptions,
}) => {
  const { session } = useStore();
  const getEmptyValueForType = (protectorType: ProtectorType): string => {
    switch (protectorType) {
      case ProtectorType.User:
        return 'Not assigned';
      case ProtectorType.Location:
        return 'Not selected';
      default:
        return '-';
    }
  };

  const choiceEntries = (): ChoiceAnswerChipEntry[] =>
    choiceOptions ? toTonedEntries(value, choiceOptions) : toLabelList(value).map(toUntonedEntry);

  const renderChoice = (id: string, entries: ChoiceAnswerChipEntry[]): React.ReactElement => {
    if (entries.length === 0) {
      return <EmptyValue id="empty-value">{getEmptyValueForType(type)}</EmptyValue>;
    }

    return choiceChips ? (
      <ChoiceAnswerChips id={id} entries={entries} />
    ) : (
      <Value id={id}>{entries.map((entry) => entry.label).join(', ')}</Value>
    );
  };

  const [lookupValue, setLookupValue] = React.useState<UuidableName | undefined>(undefined);

  React.useEffect(() => {
    // we need this function for displaying the name of lookup fields in the original incident tab
    // backend sends only the uuid of the lookup field, so we need to fetch the name of the entity
    const getValue = async (): Promise<void> => {
      if (type === ProtectorType.Lookup) {
        if (typeof value === 'string' && typeof lookupType === 'string') {
          try {
            const {
              data: { entity },
            } = await API.entities.loadEntitiesDetails(lookupType, value)();
            const nameKey = `${lookupType}__name`;
            setLookupValue({
              name: entity[nameKey].value as unknown as string,
              uuid: value,
            });
          } catch (error) {
            Logger.error(`Failed to load entity details`);
          }
        } else if (typeof value === 'object') {
          setLookupValue(value as UuidableName);
        }
      }
    };

    getValue();
  }, []);

  if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
    return <EmptyValue id="empty-value">{getEmptyValueForType(type)}</EmptyValue>;
  }
  switch (type) {
    case ProtectorType.Time:
      return <Value data-testid="field-value-time">{formatFloating(value as DateTimeValue, 'time')}</Value>;
    case ProtectorType.DateTime:
      return (
        <Value data-testid="field-value-date-time">
          {formatMoment(value as DateTimeValue, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
        </Value>
      );
    case ProtectorType.Number:
      return <Value>{value as string}</Value>;
    case ProtectorType.ShortText:
      return isImported ? (
        <PartnerAnswer label={label ?? ''} value={value as unknown as PartnerValue} />
      ) : (
        <Value id="short-text-value">{value as string}</Value>
      );
    case ProtectorType.LongText:
      return <DescriptionField id="long-text-value">{value as string}</DescriptionField>;
    case ProtectorType.SingleChoice:
      return renderChoice('single-choice-value', choiceEntries());
    case ProtectorType.MultiChoice:
      return renderChoice('multi-choice-value', choiceEntries());
    case ProtectorType.YesNoNa:
      // the server normalises the options to exactly these keys, so no form schema is needed
      return renderChoice(
        'yes-no-na-value',
        toLabelList(value).map((key) => ({ label: YES_NO_NA_LABELS[key] || key, tone: YES_NO_NA_TONE[key] }))
      );
    case ProtectorType.Date:
      return (
        <Value id="date-value" data-testid="field-value-date">
          {schema?.momentDate
            ? formatMomentDate(value as DateTimeValue)
            : formatFloating(value as DateTimeValue, 'date')}
        </Value>
      );
    case ProtectorType.User:
      return <UserRow user={value as unknown as UuidableName} />;
    case ProtectorType.Location: {
      const { address, lat, lng } = value as LocationAnswerType;
      if (lat === null && lng === null) {
        return <Value>-</Value>;
      }
      let displayValue = `Latitude: ${lat}, Longitude: ${lng}`;
      if (address) {
        displayValue = address;
      }
      return (
        <>
          <LocationOnIcon color="primary" />
          <LocationText id="location-value">{displayValue}</LocationText>
        </>
      );
    }
    case ProtectorType.Lookup: {
      // Never resolve a list over HTTP: the store writes raw uuids optimistically before the
      // autosave response lands, so fanning out would fire one request per record.
      if (Array.isArray(value)) {
        const records = resolveLookupEntries(value as unknown as LookupAnswerEntry[]);

        return (
          <div>
            {records.map((record) =>
              session.user?.role === UserRole.User ? (
                <Value key={record.uuid}>{record.name}</Value>
              ) : (
                <EntityDisplayValue key={record.uuid} link={record.name} path={`/${lookupType}/${record.uuid}`} />
              )
            )}
          </div>
        );
      }

      return session.user?.role === UserRole.User ? (
        <Value>{lookupValue?.name}</Value>
      ) : (
        <EntityDisplayValue link={lookupValue?.name} path={`/${lookupType}/${lookupValue?.uuid}`} />
      );
    }
    case ProtectorType.RiskFactor: {
      const rating = value as unknown as RiskMatrixValue;
      return isRated(rating) ? (
        <RiskMatrixDisplay value={rating} />
      ) : (
        <EmptyValue id="empty-value">{getEmptyValueForType(type)}</EmptyValue>
      );
    }
    case ProtectorType.Attachment:
      return (
        <CustomAttachmentDisplayValue
          value={value as { filename: string; size: number; url: string }[]}
          submissionTab={submissionTab}
        />
      );
    case ProtectorType.Signature:
      return <img alt="Signature" src={value as string} style={{ height: 200, objectFit: 'contain' }} />;
    case ProtectorType.RepeatingGroup:
      return repeatingGroup ? (
        <RepeatingGroupDisplay
          properties={repeatingGroup}
          entries={value as unknown as Record<string, AnswerTypes>[]}
        />
      ) : (
        <EmptyValue>-</EmptyValue>
      );
    default:
      return <Value id="not-supported-type">Field type not supported</Value>;
  }
};

export default observer(CustomFieldDisplayValue);
